// js
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import Checkbox from '../common/Checkbox';
import ConfiguredItemTitleInput from './ConfiguredItemTitleInput';
import ConfiguredItemFormField from './ConfiguredItemFormField';
import ConfiguredItemContributedCorrelationModal from './ConfiguredItemContributedCorrelationModal';
import { getVisibleConfiguredItemFormFields } from './configuredItemFormFields';
import completenessRules from './configuredItemCompletenessRules';
// css
import '../../styles/components/sources/ConfiguredItemForm.scss';


class ConfiguredItemForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      itemData: props.dsaConfiguredItem
    };

    this.saveForm = debounce(this.saveForm, 2000).bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.updateCustomConfiguredItemTitle = this.updateCustomConfiguredItemTitle.bind(this);
  }

  saveForm() {
    const { updateConfiguredItem, dsaConfiguredItem } = this.props;

    // Update and wait for resp...
    updateConfiguredItem(dsaConfiguredItem.id, this.state.itemData);
  }

  isNumberInput(input) {
    return input.getAttribute('data-type') === 'number';
  }

  isCheckboxInput(input) {
    return input.getAttribute('type') === 'checkbox';
  }

  isTextInput(input) {
    return input.getAttribute('type') === 'text';
  }

  setCompletedFlag({ persistedValue, input, item }) {
    const match = completenessRules.find(rule => (
      rule.test({
        persistedValue,
        input,
        item
      })
    ));

    if (match) {
      this.setState({
        itemData: {
          ...this.state.itemData,
          completed: match.value({ persistedValue })
        }
      });
    }
  }

  validateChange(input) {
    const value = input.value;
    const numericValue = Number(value);
    const initialValue = numericValue;

    let persistedValue;

    if (this.isNumberInput(input)) {
      persistedValue = numericValue;

      const min = parseInt(input.getAttribute('min'));
      const max = parseInt(input.getAttribute('max'));

      if (min !== null && max !== null) {
        persistedValue = Math.max(min, Math.min(persistedValue, max));
      }

      if (persistedValue !== initialValue) {
        input.value = persistedValue;
      }

    } else if (this.isCheckboxInput(input)) {
      persistedValue = input.checked;
    } else if (this.isTextInput(input)) {
      persistedValue = value;
    }

    return persistedValue;
  }

  handleChange(event) {
    const input = event.target;
    const name = input.name;
    const persistedValue = this.validateChange(input);
    const itemData = {
      ...this.state.itemData,
      [name]: persistedValue
    };

    // Update local state
    this.setState({itemData}, () => {
      this.setCompletedFlag({
        input: input,
        item: itemData,
        persistedValue
      });
    });

    const {
      changeConfiguredItem,
      dsaConfiguredItem: item
    } = this.props;

    // Save locally
    changeConfiguredItem(item.id, this.state.itemData);

    // Trigger throttled post to API
    this.saveForm();
  }

  renderCorrelationsNotice() {
    const {
      correlationCategories,
      openContributedCorrelationModal,
      closeContributedCorrelationModal,
      contributedCorrelationModalOpen,
      hasDSASecurityExtension
    } = this.props;

    if (!correlationCategories || !hasDSASecurityExtension) {
      return null;
    }

    const linkLabel =
      contributedCorrelationModalOpen ?
      'Hide List' :
      'See List';

    const linkAction =
      contributedCorrelationModalOpen ?
      closeContributedCorrelationModal :
      openContributedCorrelationModal;

    let contributedCorrelationCount = correlationCategories
      .map(category => category.correlations.filter(correlation => correlation.required).length)
      .reduce((a, b) => a + b, 0);

    if (contributedCorrelationCount > 0) {
      return (
        <div className='configured-item-form__correlations-notice'>
          <div className='row'>
            <div className='col-sm-12'>
              Contributes to {contributedCorrelationCount} of your required security correlations.
              <a onClick={linkAction}>
                {linkLabel}
              </a>
            </div>
          </div>
        </div>
      );
    }
    return <div />;
  }

  updateCustomConfiguredItemTitle(event) {
    // handle custom configured item title
    let itemData = {...this.state.itemData};
    itemData.title = event.target.value;
    this.setState({itemData});
    // save locally
    const {changeConfiguredItem, dsaConfiguredItem: item} = this.props;
    changeConfiguredItem(item.id, this.state.itemData);
    // trigger throttled post to API
    this.saveForm();
  }

  getItemDataWithDefaultValues() {
    let {
      examples,
      description,
      totalItems,
      estimatedLogSize,
      indexed,
      percentDataSourcesIndexed,
      excluded,
      completed,
      title,
      ciscoPercent
    } = this.state.itemData;

    return {
      title,
      description,
      totalItems: totalItems,
      estimatedLogSize: estimatedLogSize,
      indexed: indexed,
      percentDataSourcesIndexed: percentDataSourcesIndexed,
      examples: examples || '',
      excluded: excluded || false,
      completed: completed || false,
      ciscoPercent: (ciscoPercent === undefined || ciscoPercent === '') ? null : ciscoPercent
    };
  }

  getTabIndex(offset) {
    const { configuredItem } = this.props;
    return configuredItem.id * 1111 + configuredItem.order * 111 + offset;
  }

  render() {
    const {
      configuredItem,
      contributedCorrelationModalOpen,
      closeContributedCorrelationModal,
      correlationCategories
    } = this.props;
    const {
      excluded,
      completed,
      title
    } = this.getItemDataWithDefaultValues();
    const fields = getVisibleConfiguredItemFormFields(this.props);

    return (
      <div className={`configured-item-form ${excluded ? 'item-excluded' : ''}`}>
        <div className='configured-item-title'>
          <Checkbox
            name='completed'
            checked={completed}
            handleChange={this.handleChange}
            activeClass='icon-check-green'
            inactiveClass='icon-check-grey'
          />
          <ConfiguredItemTitleInput
            configuredItem={configuredItem}
            customTitle={title}
            onChange={this.updateCustomConfiguredItemTitle}
          />
          <div className='configured-item-form__exclude-box'>
            <Checkbox
              name='excluded'
              label='Exclude'
              checked={excluded}
              handleChange={this.handleChange}
            />
          </div>
        </div>
        <div className='configured-item-form__fields'>
          {fields.map(({ flex, label, renderInput }, index) => (
            <ConfiguredItemFormField
              flex={flex}
              label={label}
              key={index}
            >
              {renderInput({
                props: this.props,
                defaultValues: this.getItemDataWithDefaultValues(),
                tabIndex: this.getTabIndex(index),
                onChange: this.handleChange
              })}
            </ConfiguredItemFormField>
          ))}
        </div>

        {this.renderCorrelationsNotice()}

        <ConfiguredItemContributedCorrelationModal
          open={contributedCorrelationModalOpen}
          onClose={closeContributedCorrelationModal}
          correlationCategories={correlationCategories}
        />
      </div>
    );
  }
}

ConfiguredItemForm.propTypes = {
  configuredItem: PropTypes.object,
  dsaConfiguredItem: PropTypes.object,
  updateConfiguredItem: PropTypes.func,
  changeConfiguredItem: PropTypes.func,
  correlationCategories: PropTypes.array,
  openContributedCorrelationModal: PropTypes.func,
  closeContributedCorrelationModal: PropTypes.func,
  contributedCorrelationModalOpen: PropTypes.bool,
  hasDSASecurityExtension: PropTypes.bool
};

export default ConfiguredItemForm;



// WEBPACK FOOTER //
// ./src/components/sources/ConfiguredItemForm.js